// src/common/pages/dniUpload/guiaEnVivo/analizarCuadro.ts
/**
 * La guía en vivo de la cámara (decisiones del usuario, 2026-10-14): qué se ve en un
 * cuadro chico del video (~320 px) respecto del marco de la tarjeta.
 *
 * Por cada lado del marco se busca el borde del DNI en una banda angosta que lo cruza:
 * en cada columna (o fila) de la banda, el salto de brillo más fuerte; si la mayoría de
 * esos saltos caen sobre una misma recta, el borde está. Con su posición y su
 * inclinación se sabe si el lado coincide con el del marco (enciende las dos esquinas de
 * ese lado), si la tarjeta es más chica o más grande que el marco y si está torcida.
 * El reflejo son píxeles quemados dentro del marco.
 *
 * La nitidez NO se mide acá: en 320 px un desenfoque de la foto entera casi no se nota.
 * Se mide en la banda de texto, tomada del video a resolución alta (nitidezDeLaBanda).
 *
 * Funciones puras, sin DOM: se prueban con cuadros sintéticos y se calibran con las fotos
 * del corpus.
 */
import type { Rectangulo } from "../reader/types";

/** Un cuadro en grises, un byte por píxel. */
export interface CuadroGris {
  data: Uint8ClampedArray | Uint8Array;
  width: number;
  height: number;
}

/** Lo que hace falta corregir, en orden de prioridad: un solo mensaje por vez. */
export type ProblemaDelCuadro = "ubicar" | "acercar" | "alejar" | "enderezar" | "reflejo";

export interface LadoDelMarco {
  hallado: boolean;
  /** Dónde cayó el borde respecto del lado del marco, en fracciones del marco: + afuera, − adentro. */
  desvio: number;
  /** Su inclinación, en grados (horario positivo). */
  angulo: number;
}

export interface AnalisisDelCuadro {
  lados: { arriba: LadoDelMarco; derecha: LadoDelMarco; abajo: LadoDelMarco; izquierda: LadoDelMarco };
  /** Superior izquierda, superior derecha, inferior derecha, inferior izquierda. */
  esquinas: [boolean, boolean, boolean, boolean];
  /** El tamaño de la tarjeta respecto del marco (1 = justo), o null si no se puede estimar. */
  escala: number | null;
  /** La inclinación de la tarjeta, en grados, o null. */
  angulo: number | null;
  reflejo: boolean;
  /** La fracción de teselas quemadas y sin detalle dentro del marco (para calibrar). */
  quemados: number;
  problema: ProblemaDelCuadro | null;
}

/**
 * Los umbrales, calibrados con cuadros sintéticos y con las fotos del corpus puestas en el
 * marco (ver el informe, «Guía en vivo»).
 */
export const UMBRALES_DE_LA_GUIA = {
  /** Cuánto puede caer el borde afuera o adentro del lado del marco, en fracciones del marco. */
  desvioMaximo: 0.06,
  /** Cuánto más chica o más grande que el marco antes de pedir acercarla o alejarla. */
  escalaTolerada: 0.15,
  /** La inclinación tolerada, en grados. */
  anguloMaximo: 3,
  /** El salto de brillo mínimo (en niveles de 0 a 255, entre dos píxeles) para que cuente como borde. */
  saltoMinimo: 24,
  /** La fracción de columnas (o filas) del lado que tienen que caer sobre la recta. */
  apoyoMinimo: 0.65,
  /**
   * Desde qué brillo una tesela está quemada (si además no tiene detalle), y qué fracción de
   * las teselas del marco hace un reflejo.
   */
  quemado: 250,
  fraccionDeReflejo: 0.02,
  /**
   * La nitidez mínima de la banda de texto (nitidezDeLaBanda), por cara: el MRZ del dorso,
   * de letra grande y mucho relleno, da menos que el encabezado del frente. Medido con las
   * fotos del corpus puestas en el marco: enfocadas, frente 0,35-0,98 y dorso 0,16-0,54;
   * movidas (desenfoque de 3 px en 1920), frente 0,13-0,28 y dorso 0,08-0,11.
   */
  nitidezMinima: { frente: 0.3, dorso: 0.13 },
};

type Umbrales = typeof UMBRALES_DE_LA_GUIA;

/** Grises de un ImageData (RGBA). */
export function aGrises(rgba: Uint8ClampedArray, width: number, height: number): CuadroGris {
  const data = new Uint8ClampedArray(width * height);
  for (let i = 0, j = 0; j < data.length; i += 4, j++) {
    data[j] = (rgba[i] * 299 + rgba[i + 1] * 587 + rgba[i + 2] * 114) / 1000;
  }
  return { data, width, height };
}

/**
 * El borde de la tarjeta a lo largo de un lado. `valor(a, b)` lee el píxel en la posición
 * `a` a lo largo del lado y `b` a través de él.
 *
 * En cada muestra a lo largo se toman los saltos de brillo fuertes (máximos locales) del
 * perfil que cruza el lado, y se vota la recta (una Hough chica, de ±12°) con la fuerza de
 * cada salto. El borde de la tarjeta cruza todas las muestras con un salto grande; un renglón
 * de letras cerca del borde (el MRZ, al pie del dorso) da saltos fuertes solo donde hay letra,
 * y desenfocado, una franja continua pero suave. La recta tiene que cruzar además la mayoría
 * de las muestras (apoyoMinimo).
 */
function buscarBorde(
  valor: (a: number, b: number) => number,
  desdeA: number,
  hastaA: number,
  desdeB: number,
  hastaB: number,
  tolerancia: number,
  u: Umbrales
): { hallado: boolean; posicion: (a: number) => number; pendiente: number } {
  const nada = { hallado: false, posicion: () => 0, pendiente: 0 };
  const paso = Math.max(1, Math.round((hastaA - desdeA) / 120));
  const puntos: Array<{ a: number; b: number; salto: number }> = [];
  let muestras = 0;
  for (let a = desdeA; a <= hastaA; a += paso) {
    muestras++;
    let previo = 0;
    let actual = 0;
    for (let b = desdeB + 1; b < hastaB - 1; b++) {
      // Promediado con las dos vecinas a lo largo del lado: el ruido no hace un borde.
      const antes = valor(a - 1, b - 1) + valor(a, b - 1) + valor(a + 1, b - 1);
      const despues = valor(a - 1, b + 1) + valor(a, b + 1) + valor(a + 1, b + 1);
      const salto = Math.abs(despues - antes) / 3;
      // Un máximo local del salto (el de la posición anterior), si es fuerte.
      if (actual >= u.saltoMinimo && actual >= previo && actual > salto) puntos.push({ a, b: b - 1, salto: actual });
      previo = actual;
      actual = salto;
    }
  }
  if (muestras === 0 || puntos.length === 0) return nada;
  // La votación: por cada inclinación, en qué posición (al centro del lado) cae cada punto.
  const centro = (desdeA + hastaA) / 2;
  const anchoBanda = hastaB - desdeB + 1;
  let mejorVotos = 0;
  let mejorPendiente = 0;
  let mejorPosicion = 0;
  // Cada muestra vota una vez por recta, también por las dos posiciones vecinas (la recta tiene
  // grosor), con la fuerza de su salto: el borde de la tarjeta contra la mesa es más fuerte que
  // los renglones, que desenfocados se vuelven franjas continuas tan largas como el borde. Los
  // puntos llegan ordenados por muestra, así que basta recordar la última que votó.
  const votos = new Int32Array(anchoBanda + 3);
  const fuerza = new Float64Array(anchoBanda + 3);
  const ultima = new Int32Array(anchoBanda + 3);
  let mejorFuerza = 0;
  for (let grados = -12; grados <= 12; grados += 1) {
    const m = Math.tan((grados * Math.PI) / 180);
    votos.fill(0);
    fuerza.fill(0);
    ultima.fill(-1);
    for (const p of puntos) {
      const b0 = Math.round(p.b - m * (p.a - centro) - desdeB) + 1;
      for (let k = b0 - 1; k <= b0 + 1; k++) {
        if (k < 0 || k >= votos.length || ultima[k] === p.a) continue;
        ultima[k] = p.a;
        votos[k]++;
        fuerza[k] += p.salto;
      }
    }
    for (let k = 0; k < votos.length; k++) {
      if (fuerza[k] > mejorFuerza) {
        mejorFuerza = fuerza[k];
        mejorVotos = votos[k];
        mejorPendiente = m;
        mejorPosicion = k - 1 + desdeB;
      }
    }
  }
  if (mejorVotos < muestras * u.apoyoMinimo) return nada;
  // El ajuste fino, por cuadrados mínimos sobre los puntos de esa recta (uno por muestra).
  const cerca = new Map<number, { a: number; b: number; salto: number }>();
  for (const p of puntos) {
    const d = Math.abs(p.b - (mejorPosicion + mejorPendiente * (p.a - centro)));
    const ya = cerca.get(p.a);
    if (d <= tolerancia && (!ya || d < Math.abs(ya.b - (mejorPosicion + mejorPendiente * (ya.a - centro))))) cerca.set(p.a, p);
  }
  const elegidos = [...cerca.values()];
  if (elegidos.length < Math.max(2, muestras * u.apoyoMinimo)) return nada;
  const n = elegidos.length;
  const sa = elegidos.reduce((s, p) => s + p.a, 0);
  const sb = elegidos.reduce((s, p) => s + p.b, 0);
  const saa = elegidos.reduce((s, p) => s + p.a * p.a, 0);
  const sab = elegidos.reduce((s, p) => s + p.a * p.b, 0);
  const den = n * saa - sa * sa;
  if (den === 0) return nada;
  const m = (n * sab - sa * sb) / den;
  const c = (sb - m * sa) / n;
  return { hallado: true, posicion: (a: number) => m * a + c, pendiente: m };
}

/** Lo que se ve en el cuadro respecto del marco (en píxeles del cuadro). */
export function analizarCuadro(g: CuadroGris, marco: Rectangulo, u: Umbrales = UMBRALES_DE_LA_GUIA): AnalisisDelCuadro {
  const { width: W, height: H, data } = g;
  const px = (x: number, y: number) => {
    const xx = x < 0 ? 0 : x >= W ? W - 1 : x;
    const yy = y < 0 ? 0 : y >= H ? H - 1 : y;
    return data[yy * W + xx];
  };
  const { x: mx, y: my, w: mw, h: mh } = marco;
  const recortar = (v: number, max: number) => Math.max(0, Math.min(max, Math.round(v)));
  // La banda que cruza cada lado: un cuarto del marco hacia afuera y un tercio hacia adentro
  // (una tarjeta de hasta el 40 % menos todavía se ve). A lo largo, sin las puntas redondeadas.
  const AFUERA = 0.25;
  const ADENTRO = 0.33;
  const PUNTA = 0.12;
  const tolV = Math.max(2, mh * 0.025);
  const tolH = Math.max(2, mw * 0.025);

  const horizontal = (yLado: number, signo: 1 | -1): LadoDelMarco => {
    // signo: −1 arriba (afuera es hacia arriba), +1 abajo.
    const b0 = signo < 0 ? yLado - mh * AFUERA : yLado - mh * ADENTRO;
    const b1 = signo < 0 ? yLado + mh * ADENTRO : yLado + mh * AFUERA;
    const r = buscarBorde(
      (a, b) => px(a, b),
      recortar(mx + mw * PUNTA, W - 1),
      recortar(mx + mw * (1 - PUNTA), W - 1),
      recortar(b0, H - 1),
      recortar(b1, H - 1),
      tolV,
      u
    );
    if (!r.hallado) return { hallado: false, desvio: 0, angulo: 0 };
    const enElCentro = r.posicion(mx + mw / 2);
    return {
      hallado: true,
      desvio: (signo < 0 ? yLado - enElCentro : enElCentro - yLado) / mh,
      angulo: (Math.atan(r.pendiente) * 180) / Math.PI,
    };
  };
  const vertical = (xLado: number, signo: 1 | -1): LadoDelMarco => {
    const b0 = signo < 0 ? xLado - mw * AFUERA : xLado - mw * ADENTRO;
    const b1 = signo < 0 ? xLado + mw * ADENTRO : xLado + mw * AFUERA;
    const r = buscarBorde(
      (a, b) => px(b, a),
      recortar(my + mh * PUNTA, H - 1),
      recortar(my + mh * (1 - PUNTA), H - 1),
      recortar(b0, W - 1),
      recortar(b1, W - 1),
      tolH,
      u
    );
    if (!r.hallado) return { hallado: false, desvio: 0, angulo: 0 };
    const enElCentro = r.posicion(my + mh / 2);
    return {
      hallado: true,
      desvio: (signo < 0 ? xLado - enElCentro : enElCentro - xLado) / mw,
      // x = m·y + c: girada en sentido horario, el lado izquierdo baja hacia la izquierda (m < 0).
      angulo: (-Math.atan(r.pendiente) * 180) / Math.PI,
    };
  };

  const lados = {
    arriba: horizontal(my, -1),
    derecha: vertical(mx + mw, 1),
    abajo: horizontal(my + mh, 1),
    izquierda: vertical(mx, -1),
  };
  const coincide = (l: LadoDelMarco) =>
    l.hallado && Math.abs(l.desvio) <= u.desvioMaximo && Math.abs(l.angulo) <= u.anguloMaximo;
  const esquinas: [boolean, boolean, boolean, boolean] = [
    coincide(lados.arriba) && coincide(lados.izquierda),
    coincide(lados.arriba) && coincide(lados.derecha),
    coincide(lados.abajo) && coincide(lados.derecha),
    coincide(lados.abajo) && coincide(lados.izquierda),
  ];

  const escalas: number[] = [];
  if (lados.izquierda.hallado && lados.derecha.hallado) escalas.push(1 + lados.izquierda.desvio + lados.derecha.desvio);
  if (lados.arriba.hallado && lados.abajo.hallado) escalas.push(1 + lados.arriba.desvio + lados.abajo.desvio);
  // Una tarjeta más grande que la foto en un sentido deja un solo par de lados: alcanza. Con los
  // dos pares, el mayor: cuando un borde queda fuera de la foto, su lado agarra un renglón de
  // adentro y achica la estimación; agrandarla no puede (afuera está la mesa).
  const escala = escalas.length ? Math.max(...escalas) : null;
  const hallados = Object.values(lados).filter((l) => l.hallado);
  const angulo = hallados.length ? hallados.reduce((s, l) => s + l.angulo, 0) / hallados.length : null;

  // El reflejo: teselas quemadas Y sin detalle dentro del marco, como en la calidad de la foto
  // (quality.ts, reflejo). Contar píxeles claros no sirve: el papel del DNI es claro y, con la
  // exposición automática, da 250 o más en buena parte sin ningún reflejo; lo que el brillo
  // sobre el plastificado hace además es borrar el texto.
  const tesela = Math.max(4, Math.round(mh / 20));
  const x0 = recortar(mx + mw * 0.03, W - 1);
  const x1 = recortar(mx + mw * 0.97, W - 1);
  const y0 = recortar(my + mh * 0.03, H - 1);
  const y1 = recortar(my + mh * 0.97, H - 1);
  let teselas = 0;
  let cegadas = 0;
  for (let ty = y0; ty + tesela <= y1; ty += tesela)
    for (let tx = x0; tx + tesela <= x1; tx += tesela) {
      let suma = 0;
      let variacion = 0;
      for (let y = ty; y < ty + tesela; y++)
        for (let x = tx; x < tx + tesela; x++) {
          const v = data[y * W + x];
          suma += v;
          if (x > tx) variacion += Math.abs(v - data[y * W + x - 1]);
        }
      const n = tesela * tesela;
      teselas++;
      if (suma / n >= u.quemado && variacion / n < 2) cegadas++;
    }
  const fraccion = teselas ? cegadas / teselas : 0;
  const reflejo = fraccion > u.fraccionDeReflejo;

  let problema: ProblemaDelCuadro | null = null;
  if (hallados.length < 2) problema = "ubicar";
  else if (escala !== null && escala < 1 - u.escalaTolerada) problema = "acercar";
  else if (escala !== null && escala > 1 + u.escalaTolerada) problema = "alejar";
  else if (angulo !== null && Math.abs(angulo) > u.anguloMaximo) problema = "enderezar";
  else if (!esquinas.every(Boolean)) problema = "ubicar";
  else if (reflejo) problema = "reflejo";

  return { lados, esquinas, escala, angulo, reflejo, quemados: fraccion, problema };
}

/**
 * La nitidez de una banda de texto: el desvío del laplaciano sobre el desvío del brillo.
 * Al dividir, no depende del contraste ni de la luz; un texto enfocado da bordes duros
 * (laplaciano alto) y uno movido o desenfocado, suaves. Una banda lisa da 0.
 */
export function nitidezDeLaBanda(g: CuadroGris): number {
  const { width: W, height: H, data } = g;
  if (W < 3 || H < 3) return 0;
  let s = 0;
  let ss = 0;
  let sl = 0;
  let ssl = 0;
  let n = 0;
  for (let y = 1; y < H - 1; y++)
    for (let x = 1; x < W - 1; x++) {
      const i = y * W + x;
      const v = data[i];
      const l = data[i - 1] + data[i + 1] + data[i - W] + data[i + W] - 4 * v;
      s += v;
      ss += v * v;
      sl += l;
      ssl += l * l;
      n++;
    }
  const varBrillo = ss / n - (s / n) ** 2;
  const varLap = ssl / n - (sl / n) ** 2;
  if (varBrillo < 4) return 0;
  return Math.sqrt(Math.max(0, varLap)) / Math.sqrt(varBrillo);
}
